import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthContext";
import { roleLabels, roleBadgeColors, stageBadgeColors } from "@/mocks/authData";
import { getColorClass } from "@/mocks/projectsData";
import type { Role } from "@/mocks/authData";
import CrmLayout from "@/components/feature/CrmLayout";
import TodaysAlertsBar from "./components/TodaysAlertsBar";
import PipelineHealthFunnel from "./components/PipelineHealthFunnel";
import { fetchApi } from "@/services/apiService";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [leads, setLeads] = useState<any[]>([]);
  const [calls, setCalls] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [stats, setStats] = useState([
    { label: "Total Leads", value: "0", change: "+12.5%", up: true, icon: "ri-user-add-line", color: "bg-green-50 text-green-600" },
    { label: "Deals Won", value: "0", change: "+8.2%", up: true, icon: "ri-trophy-line", color: "bg-blue-50 text-blue-600" },
    { label: "Active Projects", value: "0", change: "Active", up: true, icon: "ri-building-line", color: "bg-indigo-50 text-indigo-600" },
    { label: "Pipeline Value", value: "₹0", change: "+5.0%", up: true, icon: "ri-money-rupee-circle-line", color: "bg-violet-50 text-violet-600" },
  ]);

  useEffect(() => {
    // 1. Fetch Dynamic Leads
    fetchApi<any>('/leads?limit=5', undefined, null)
      .then((res) => {
        const rawItems = res?.items || res?.data?.items || (Array.isArray(res) ? res : []);
        if (Array.isArray(rawItems)) {
          const formatted = rawItems.map((l: any) => ({
            id: l.id,
            name: l.name,
            company: l.company || 'Direct Client',
            value: l.value || '₹0',
            stage: l.stage || 'New Inquiry',
            projectId: l.projectId || l.project_id,
            projectName: l.projectName,
            avatar: l.name ? l.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() : 'LD'
          }));
          setLeads(formatted);
        }
      })
      .catch((err) => console.error("Error loading dashboard leads:", err));

    // 2. Fetch Dynamic Projects
    fetchApi<any[]>('/projects', undefined, [])
      .then((projList) => {
        const list = Array.isArray(projList) ? projList : (projList as any)?.data || [];
        setProjects(list.slice(0, 4));

        const activeCount = list.filter((p: any) => p.status === "Active").length;
        const totalBudgetSum = list.reduce((sum: number, p: any) => sum + (Number(p.budget) || 0), 0);

        setStats((prev) =>
          prev.map((st) => {
            if (st.label === "Active Projects") return { ...st, value: String(activeCount) };
            if (st.label === "Pipeline Value" && totalBudgetSum > 0) {
              return { ...st, value: `₹${totalBudgetSum.toLocaleString('en-IN')}` };
            }
            return st;
          })
        );
      })
      .catch((err) => console.error("Error loading dashboard projects:", err));

    // 3. Fetch Dynamic Calls
    fetchApi<any>('/calls', undefined, null)
      .then((callsRes) => {
        const rawCalls = Array.isArray(callsRes)
          ? callsRes
          : Array.isArray(callsRes?.data)
          ? callsRes.data
          : Array.isArray(callsRes?.items)
          ? callsRes.items
          : [];
        if (rawCalls.length > 0) {
          const formatted = rawCalls.slice(0, 3).map((c: any) => ({
            contact: c.leadName || c.contact || "Customer",
            company: c.company || c.phone || "CRM Contact",
            time: c.callTime || c.time || "Scheduled",
            type: c.callType || c.type || "Follow-up",
          }));
          setCalls(formatted);
        }
      })
      .catch(() => {});

    // 4. Fetch Dynamic Lead Stats
    fetchApi<{ total?: number; converted?: number }>('/leads/stats', undefined, null)
      .then((statData) => {
        if (statData && statData.total !== undefined) {
          setStats((prev) =>
            prev.map((st) => {
              if (st.label === "Total Leads") return { ...st, value: String(statData.total) };
              if (st.label === "Deals Won") return { ...st, value: String(statData.converted || 0) };
              return st;
            })
          );
        }
      })
      .catch((err) => console.error("Error loading dashboard stats:", err));
  }, []);

  if (!user) return null;

  return (
    <CrmLayout title="Dashboard">
      <div className="p-5 md:p-6 space-y-6 max-w-[1600px] mx-auto">
        {/* Welcome section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm text-gray-500 mb-0.5">
              {(() => {
                const hour = new Date().getHours();
                if (hour < 12) return "Good morning";
                if (hour < 17) return "Good afternoon";
                return "Good evening";
              })()}
            </p>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome, {user.name.split(" ")[0]}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Here&apos;s what&apos;s happening with your sales pipeline, projects, and leads today.
            </p>
          </div>
          <span
            className={`self-start px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${roleBadgeColors[user.role as Role]}`}
          >
            {roleLabels[user.role as Role]}
          </span>
        </div>

        {/* Dynamic Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5 hover:border-indigo-300 hover:shadow-sm transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {stat.label}
                </span>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <i className={`${stat.icon} text-base`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <span className={`text-xs font-medium ${stat.up ? "text-green-600" : "text-gray-500"}`}>
                {stat.change}
              </span>
            </div>
          ))}
        </div>

        {/* Active Projects Live Section */}
        {projects.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <i className="ri-building-line text-sm" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Active Projects &amp; Inventory Tracker</h3>
              </div>
              <button
                onClick={() => navigate("/projects")}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
              >
                View all projects →
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {projects.map((proj) => (
                <div
                  key={proj.id}
                  onClick={() => navigate(`/projects/${proj.id}`)}
                  className="p-4 rounded-xl border border-gray-200 hover:border-indigo-400 hover:shadow-md transition-all cursor-pointer bg-white group flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${getColorClass(proj.color || 'indigo')}`} />
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {proj.status || "Active"}
                      </span>
                    </div>
                    <h4 className="text-sm font-bold text-gray-900 group-hover:text-indigo-600 transition-colors truncate">
                      {proj.name}
                    </h4>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {proj.client_name ? `Client: ${proj.client_name}` : proj.category || "Real Estate"}
                    </p>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-500">{proj.leads || 0} Leads</span>
                      <span className="font-bold text-gray-900">{proj.progress || 0}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${getColorClass(proj.color || 'indigo')}`}
                        style={{ width: `${Math.min(100, proj.progress || 0)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Recent leads table */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Recent Leads</h3>
              <button
                onClick={() => navigate("/leads")}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap cursor-pointer"
              >
                View all
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Lead</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Project</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-5 py-8 text-center text-xs text-gray-400">
                        No leads recorded yet.
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => (
                      <tr
                        key={lead.id || lead.name}
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        className="hover:bg-indigo-50/40 transition-colors cursor-pointer"
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {lead.avatar}
                            </div>
                            <span className="font-medium text-gray-900 whitespace-nowrap">{lead.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-gray-600 whitespace-nowrap">{lead.company}</td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          {lead.projectName || lead.projectId ? (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                if (lead.projectId) navigate(`/projects/${lead.projectId}`);
                              }}
                              className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer"
                            >
                              🏢 {lead.projectName || "Project"}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${stageBadgeColors[lead.stage] || "bg-gray-100 text-gray-600"}`}>
                            {lead.stage}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Upcoming calls */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Upcoming &amp; Recent Calls</h3>
              <button
                onClick={() => navigate("/calls")}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap cursor-pointer"
              >
                View all
              </button>
            </div>
            <div className="p-5 space-y-4">
              {calls.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No call records found.</p>
              ) : (
                calls.map((call, idx) => (
                  <div key={call.contact + idx} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center text-xs font-bold text-indigo-600 flex-shrink-0 mt-0.5">
                      {call.contact.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{call.contact}</p>
                      <p className="text-xs text-gray-500 truncate">{call.company}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs text-gray-600 flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-time-line" />
                          {call.time}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 whitespace-nowrap">
                          {call.type}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Today's Alerts */}
        <TodaysAlertsBar />

        {/* Pipeline Health */}
        <PipelineHealthFunnel />
      </div>
    </CrmLayout>
  );
}